import { Connection } from '@omneo/omneo-sdk'
import Icon from '@/components/snippets/icon/icon'
import PreactImage from '@/components/snippets/image/image'
import { useCallback, useEffect, useMemo, useState } from 'preact/hooks'
import { AccountKidProfile, PROFILE_ICONS } from './AccountKidProfile'
import { AccountBanner } from './AccountBanner'
import { Suspense } from './Suspense'
import { useAccountLayoutContext } from './AccountProfileContext'
import { AccountKidProfileNew } from './AccountKidProfileNew'
import { PageModal } from './AccountModal'
import { useToggle } from '../hooks/useToggle'
import { FormAddMeasurements } from './FormAddMeasurements'
import { Page } from './AccountPage'
import { useQueryState } from '@/components/hooks/useQueryState'
import { getKidsProfiles } from '../helpers/getKidsProfiles'
import { AssignShoesModal } from './AssignShoesModal'
import { accountTranslations } from '../helpers/translations'

/**
 *  @typedef {import("../types/omneo").OmneoConnection} OmneoConnection
 *  @typedef {import('../types/omneo').ChildProfile} ChildProfile
 */

/**
 *
 *
 *
 * @param {{icon: string}} param0
 * @returns
 */
const KidsProfileIcon = ({ icon }) => {
  return (
    <div data-role="icon-title" className={'flex flex-row gap-4'}>
      <div className="w-16 h-16">
        <PreactImage
          imageClass="fit object-bottom"
          className={'h-full w-full object-cover'}
          key={'id'}
          imageUrl={[icon]}
        />
      </div>
    </div>
  )
}

/**
 *
 * @param {string} param0
 * @returns
 */
const KidsProfileEditButton = ({ id, setViewFullProfile }) => {
  return (
    <div
      onClick={setViewFullProfile}
      className={
        'relative my-auto flex aspect-1 h-fit min-w-11 cursor-pointer items-center justify-center rounded-full bg-primary-black'
      }
    >
      <Icon id={'edit'} className="text-primary-white" />
    </div>
  )
}

/**
 *
 * @param {{firstName: string, shoeTag: string}} param0
 * @returns
 */
const KidsProfileNameAndTag = ({ firstName, shoeTag }) => {
  return (
    <div className={'flex flex-col'}>
      <h4 className={'h4 font-bold'}>{firstName}'s Profile</h4>
      <p className={'paragraph-sm text-neutrals-secondary-grey'}>{shoeTag}</p>
    </div>
  )
}

/**
 *
 * @param {{profile: ChildProfile}} param0
 */
const KidsProfileHeader = ({ profile, setViewFullProfile }) => {
  return (
    <div className={'flex flex-row gap-3'}>
      <KidsProfileIcon icon={profile?.avatar_url} />
      <div className={'flex w-full flex-row justify-between'}>
        <KidsProfileNameAndTag
          firstName={profile.first_name}
          shoeTag={profile.identities.find((identity) => identity.handle === 'shoetag')?.identifier ?? ''}
        />
        <KidsProfileEditButton id={profile.id} setViewFullProfile={setViewFullProfile} />
      </div>
    </div>
  )
}

/**
 *
 * @param {ChildProfile} profile
 * @returns
 */
const getRecentMeasurements = (profile) => {
  if (!profile.measurements?.length) {
    return { size_shoe: { value: '' }, width_shoe: { value: '' }, date: '', rawDate: '' }
  }
  const measurement = profile.measurements.sort((a, b) => new Date(b.rawDate) - new Date(a.rawDate))[0]
  return measurement
}

/**
 *
 * @param {{profile: ChildProfile}} param0
 */
const KidsProfileDetails = ({ profile, handleToggle }) => {
  const measurement = getRecentMeasurements(profile)
  const measurementDate = measurement.date
    ? new Date(measurement.date).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
    : ''
  const formattedBirthDate = profile.birth_month
    ? `${Intl.DateTimeFormat('en-AU', { month: 'long', year: 'numeric' }).format(
        new Date(profile.birth_year, profile.birth_month - 1, 1)
      )}`
    : ''

  return (
    <div className={'flex flex-col'}>
      <div>
        <b>{accountTranslations.form_labels.birthday}:</b> {formattedBirthDate}
      </div>
      <div>
        <b>{accountTranslations.form_labels.size}:</b>{' '}
        {!measurement.size_shoe?.value ? (
          <span className={'underline'} onClick={handleToggle}>
            {accountTranslations.kids_profiles.set_size}
          </span>
        ) : (
          <>
            {measurement.size_shoe?.value} (measured on {measurementDate})
          </>
        )}
      </div>
      <div>
        <b>{accountTranslations.form_labels.width}:</b>{' '}
        {!measurement.width_shoe?.value ? (
          <span className={'underline'} onClick={handleToggle}>
            {accountTranslations.kids_profiles.set_width}
          </span>
        ) : (
          <>
            {measurement.width_shoe?.value} (measured on {measurementDate})
          </>
        )}
      </div>
    </div>
  )
}

/**
 *
 * @param {{profile: Connection}} param0
 */
const KidsProfileCard = ({ profile, handleViewProfile, cache }) => {
  const { isActive, handleToggle } = useToggle()
  const [child, setChild] = useState(profile)

  const setViewFullProfile = () => {
    handleViewProfile('selected', { child: profile, connectionId: profile.connectionId })
  }

  const mutatedChildData = useMemo(() => {
    if (!child) return null
    // Get PROFILE_ICONS based on `childProfile.avatar_url`
    const icon = PROFILE_ICONS.find((icon) => `https:${icon.icon}` === child.avatar_url)
    return { ...child, ...(icon ? { backgroundColour: icon.background_colour } : {}) }
  }, [child])

  return (
    <Suspense
      loadingState={
        <div className={'flex h-full min-h-52 w-full animate-breathe flex-col  gap-4 bg-secondary-yellow p-10'}></div>
      }
      props={{
        skipLoad: true,
        asyncCallback: async () => {}
      }}
    >
      <PageModal
        props={{
          isActive,
          handleToggle,
          modalTitle: 'Measurements',
          modal: <FormAddMeasurements handleToggle={handleToggle} handleUpdateView={setViewFullProfile} connectionId={profile.connectionId} />
        }}
      >
        <div
          style={{
            '--background-color': mutatedChildData?.backgroundColour ?? '#fdc145'
          }}
          className={'flex h-full w-full flex-col gap-4 bg-[var(--background-color)] p-10'}
        >
          <KidsProfileHeader profile={profile} setViewFullProfile={setViewFullProfile} />
          <div className={' border border-solid border-neutrals-secondary-grey opacity-50'}></div>
          <KidsProfileDetails profile={profile} handleToggle={handleToggle} />
          <div onClick={setViewFullProfile} className={'btn btn-inline btn-text underline underline-offset-2'}>
            {accountTranslations.kids_profiles.view_full_profile}
          </div>
        </div>
      </PageModal>
    </Suspense>
  )
}

const RewardsFullBanner = ({ childrenProfiles }) => {
  const { omneoClient } = useAccountLayoutContext()
  const { isActive, handleToggle } = useToggle()
  const [sectionData, setSectionData] = useState({
    itemsToAssign: [],
    itemsToClaim: []
  })

  return (
    <Suspense
      props={{
        asyncCallback: async () => {
          const lines = await omneoClient.profileClient.getUnassignedTransactionItems({ filterOutExpired: false, filterReturned: true })
          const unexpiredUnassignedItems = await omneoClient.profileClient.getUnassignedTransactionItems({ filterOutExpired: true, filterReturned: true })

          setSectionData(() => ({ itemsToAssign: [...lines], itemsToClaim: [...unexpiredUnassignedItems] }))
        }
      }}
    >
      {!!sectionData.itemsToAssign?.length && (
        <AssignShoesModal
          childrenProfiles={childrenProfiles}
          unassignedItems={sectionData.itemsToClaim}
          isActive={isActive}
          handleToggle={handleToggle}
          handleRefetch={() => {
            window.location.reload()
          }}
        >
          <AccountBanner
            className={'origin-top-left rotate-1 bg-secondary-paleblue bg-opacity-[0.24]'}
            title={`You have ${sectionData.itemsToAssign?.length} pair${sectionData.itemsToAssign?.length === 1 ? '' : 's'} of shoes to assign.`}
            body={"If they were purchased in the last 30 days, you'll receive $2 rewards for each pair assigned."}
            button={
              <div onClick={handleToggle} className={'btn btn-solid-black btn-md w-fit'}>
                {sectionData.itemsToClaim?.length > 0
                  ? 'Assign shoes'
                  : accountTranslations.my_rewards.history.cta
                }
              </div>
            }
          />
        </AssignShoesModal>
      )}
    </Suspense>
  )
}

const AccountKidsBanners = ({ handleNewProfile, childrenProfiles }) => {
  return (
    <div className={'flex flex-col gap-0 py-8 perspective-0'}>
      <AccountBanner
        className={'origin-bottom-left -rotate-1 bg-secondary-violet/30'}
        title={accountTranslations.kids_profiles.add_profile_banner_title}
        body={''}
        cta={{
          title: accountTranslations.kids_profiles.add_profile_banner_cta,
          callback: handleNewProfile
        }}
      />

      <RewardsFullBanner childrenProfiles={childrenProfiles} />
    </div>
  )
}

export const AccountKidProfiles = () => {
  const [activeChild, setActiveChild] = useQueryState('child')
  const { omneoClient, cacheConfig } = useAccountLayoutContext()
  const [profileConfig, setProfileConfig] = useState({
    selected: null,
    profiles: cacheConfig.cacheData.ProfileConnections ?? []
  })

  /**
   *
   * @param {'selected' | 'profiles'} key
   * @param {any} value
   * @returns
   */
  const handleProfileConfig = useCallback((key, value) => {
    if (key === 'selected') {
      setActiveChild(value ? value.child.connectionId : undefined)
    }
    // If key isn't selected and active child is set, set the selected profile to the active child
    if (key !== 'selected' && activeChild) {
      const selectedChild = { child: value.find((p) => p.connectionId.toString() === activeChild), connectionId: activeChild }
      return setProfileConfig((c) => ({ ...c, [key]: value, selected: selectedChild }))
    }
    return setProfileConfig((c) => ({ ...c, [key]: value }))
  }, [activeChild])

  const handleBack = () => setActiveChild(undefined)

  const handleNewProfile = () => {
    setActiveChild('new')
    setProfileConfig((config) => ({
      ...config,
      selected: {
        child: {
          first_name: 'New Profile'
        },
        isNewProfile: true,
        connectionId: null
      }
    }))
  }

  useEffect(() => {
    // Handles going back from editing or creating a new profile
    if (!activeChild) {
      setProfileConfig((c) => {
        return { ...c, selected: null }
      })
    }

    document?.querySelector('#top')?.scrollIntoView({ behavior: 'instant' })
  }, [activeChild])

  const fetchChildrenProfiles = useCallback(async () => {
    // Fetch profiles
    const profiles = await omneoClient.profileClient.getChildren()
    const kidsProfiles = await getKidsProfiles(
      omneoClient,
      profiles.map((profile) => profile.id)
    )

    handleProfileConfig('profiles', kidsProfiles)
    cacheConfig.updateCacheConfig('ChildrenProfiles', kidsProfiles)
    cacheConfig.updateCacheConfig('ProfileConnections', profiles)
  }, [])

  if (profileConfig.selected?.child) {
    if (profileConfig.selected?.isNewProfile) {
      return <AccountKidProfileNew handleBack={handleBack} />
    }

    return (
      <AccountKidProfile
        profile={profileConfig.selected?.child}
        connectionId={profileConfig.selected?.connectionId}
        isNewProfile={false}
        handleBack={handleBack}
      />
    )
  }

  return (
    <Page
      props={{
        page: {
          title: accountTranslations.kids_profiles.title
        },
        suspense: {
          asyncCallback: fetchChildrenProfiles,
          loadingState: (
            <div className="grid gap-8 xl:grid-cols-2 xl:gap-10 3xl:grid-cols-3">
              <div className="h-[290px] animate-pulse bg-primary-grey" />
              <div className="h-[290px] animate-pulse bg-primary-grey" />
              <div className="h-[290px] animate-pulse bg-primary-grey" />
              <div className="h-[290px] animate-pulse bg-primary-grey" />
              <div className="h-[290px] animate-pulse bg-primary-grey" />
              <div className="h-[290px] animate-pulse bg-primary-grey" />
            </div>
          )
        }
      }}
    >
      <div className={'grid gap-8 xl:grid-cols-2 xl:gap-10 3xl:grid-cols-3'}>
        {profileConfig.profiles.map((profile) => (
          <KidsProfileCard profile={profile} handleViewProfile={handleProfileConfig} cache={cacheConfig} />
        ))}
      </div>
      <AccountKidsBanners handleNewProfile={handleNewProfile} childrenProfiles={profileConfig.profiles} />
    </Page>
  )
}
